import CareerCarousel from "@crescentParts/Components/CareerCarousel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkListStyles } from "@insite/content-library/Widgets/Basic/LinkList";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import React, { FC } from "react";
import { css } from "styled-components";

interface MemberList {
    fields: {
        img: string;
        imgtext: string;
    };
}

const enum fields {
    divisions = "divisions",
    imagesPerRow = "imagesPerRow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.divisions]: MemberList[];
        [fields.imagesPerRow]: number;
    };
}

export interface PhotoStyle {
    imagearea: InjectableCss;
    mainContainer: InjectableCss;
    imagetext: InjectableCss;
    image: InjectableCss;
    linkList?: LinkListStyles;
    modal?: ModalPresentationProps;
}

export const photoStyle: PhotoStyle = {
    mainContainer: {
        css: css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            column-gap: 20px;
            row-gap: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: repeat(1, 1fr);
                    `,
                    css`
                        grid-template-columns: repeat(1, 1fr);
                    `,
                    css`
                        grid-template-columns: repeat(2, 1fr);
                    `,
                    css``,
                    css``,
                ])}
        `,
    },

    imagearea: {
        css: css`
            display: flex;
            justify-content: flex-start;
            flex-direction: column;
            gap: 15px;
            border-radius: 6px 6px 6px 6px;
            overflow: hidden;
            [class^="LazyImageStyle"] {
                height: unset;
            }
        `,
    },
    image: {
        css: css`
            max-width: 100%;
            height: 100%;
            cursor: pointer;
        `,
    },

    imagetext: {
        css: css`
            display: flex;
            color: black;
        `,
    },

    modal: {
        size: 1600,

        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
                [class^="ButtonWrapper"] {
                    border: solid 2px;
                    border-radius: 50%;
                }
                svg {
                    height: 20px;
                    width: 20px;
                }
                [class^="IconWrapper"] {
                    top: 0em;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            `,
            modalContent: css`
                padding: 20px;
                height: calc(100vh - 57px);
                [class^="sc-bczRLJ devMrE"] {
                    padding: 0px;
                }
            `,
            modalContainer: css`
                height: 100vh;
                [class^="ContentBody"] {
                    max-height: 100vh;
                }
                [class^="ModalContent"] {
                    padding: 16px;
                }
            `,
        },
    },
};

const styles = photoStyle;

export const ImageGalleryPopUp: FC<OwnProps> = ({ fields }) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [selectedImgText, setSelectedImgText] = React.useState("");
    const [activeCarouselImg, setActiveCarouselImg] = React.useState(0);
    const modalOpenHandler = (imgtext: string) => {
        setSelectedImgText(imgtext);
        setModalIsOpen(true);
    };
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const containerStyles = {
        css: css`
            display: grid;
            grid-template-columns: repeat(${fields.imagesPerRow}, 1fr);
            column-gap: 20px;
            row-gap: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: repeat(1, 1fr);
                    `,
                    css`
                        grid-template-columns: repeat(1, 1fr);
                    `,
                    css`
                        grid-template-columns: repeat(2, 1fr);
                    `,
                    css``,
                    css``,
                ])}
        `,
    };

    return (
        <>
            <StyledWrapper {...containerStyles}>
                {fields.divisions?.map((item, index) => {
                    return (
                        <StyledWrapper {...styles.imagearea} key={item.fields.img + index}>
                            <LazyImage
                                {...styles.image}
                                src={item.fields.img}
                                onClick={() => {
                                    setActiveCarouselImg(index);
                                    modalOpenHandler(item.fields.imgtext);
                                }}
                            ></LazyImage>
                            <StyledWrapper {...styles.imagetext}>{item.fields.imgtext}</StyledWrapper>
                        </StyledWrapper>
                    );
                })}
                <Modal
                    {...styles.modal}
                    headline={selectedImgText}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <CareerCarousel
                        carouselData={fields.divisions}
                        activeCarouselImg={activeCarouselImg}
                        setActiveCarouselImg={setActiveCarouselImg}
                        selectedImgText={selectedImgText}
                        setSelectedImgText={setSelectedImgText}
                    />
                </Modal>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: ImageGalleryPopUp,
    definition: {
        group: "Basic",
        displayName: "Image Gallery Popup",
        fieldDefinitions: [
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.imgtext;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "img",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imgtext",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                fieldType: "General",
                name: fields.imagesPerRow,
                editorTemplate: "IntegerField",
                defaultValue: 3,
            },
        ],
    },
};

export default widgetModule;
